import React, { useEffect, useState } from 'react';
import {
  Alert,
  Drawer,
  Form,
  Input,
  Switch,
  Button,
  Space,
  List,
  Divider,
  InputNumber,
  Select,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import VariablesHelper from './VariablesHelper';
import { isTableSectionWidthsValid, sumColumnWidths, isWidthSumValid } from './utils/tableWidths';
import { DATE_FORMAT_PRESETS } from './dateFormats';

const cloneSection = (s) => JSON.parse(JSON.stringify(s));

function DataGridEditor({ value, onChange }) {
  const fields = value?.fields || [];
  const update = (idx, patch) => {
    const next = fields.map((f, i) => (i === idx ? { ...f, ...patch } : f));
    onChange({ ...value, fields: next });
  };
  const remove = (idx) => onChange({ ...value, fields: fields.filter((_, i) => i !== idx) });
  const add = () =>
    onChange({
      ...value,
      fields: [...fields, { key: '', label: 'Nuevo campo', visible: true }],
    });

  return (
    <>
      <List
        dataSource={fields}
        renderItem={(field, idx) => (
          <List.Item>
            <Space style={{ width: '100%' }}>
              <Input
                placeholder="key"
                value={field.key}
                onChange={(e) => update(idx, { key: e.target.value })}
                style={{ width: 150 }}
              />
              <Input
                placeholder="label"
                value={field.label}
                onChange={(e) => update(idx, { label: e.target.value })}
                style={{ width: 200 }}
              />
              <Select
                placeholder="Formato"
                value={field.dateFormat || ''}
                onChange={(val) => update(idx, { dateFormat: val || undefined })}
                options={DATE_FORMAT_PRESETS}
                style={{ width: 230 }}
                title="Formato de fecha (si el campo es una fecha)"
              />
              <Switch
                checked={field.visible !== false}
                onChange={(checked) => update(idx, { visible: checked })}
              />
              <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(idx)} />
            </Space>
          </List.Item>
        )}
      />
      <Button icon={<PlusOutlined />} onClick={add} block>
        Agregar campo
      </Button>
    </>
  );
}

function TextEditor({ value, onChange, documentType, tenantId }) {
  return (
    <>
      <Form.Item label="Cuerpo (soporta variables)">
        <Input.TextArea
          rows={6}
          value={value?.body || ''}
          onChange={(e) => onChange({ ...value, body: e.target.value })}
        />
      </Form.Item>
      <VariablesHelper documentType={documentType} tenantId={tenantId} />
    </>
  );
}

const COLUMN_TYPE_OPTIONS = [
  { label: 'Texto', value: 'text' },
  { label: 'Número', value: 'number' },
  { label: 'Moneda', value: 'money' },
  { label: 'Fecha', value: 'date' },
];

function TableEditor({ value, onChange }) {
  const columns = value?.columns || [];
  const computedColumns = value?.computedColumns || [];
  const update = (idx, patch) => {
    const next = columns.map((c, i) => (i === idx ? { ...c, ...patch } : c));
    onChange({ ...value, columns: next });
  };
  const remove = (idx) => onChange({ ...value, columns: columns.filter((_, i) => i !== idx) });
  const add = () =>
    onChange({
      ...value,
      columns: [...columns, { key: '', title: 'Columna', width: '25%' }],
    });

  // La suma de widths debe ser 100% para que la tabla ocupe todo el ancho del
  // PDF. Si no, el motor deja un gap visual a la derecha. El editor muestra
  // la suma en vivo y SectionEditor bloquea "Aplicar" hasta regularizar.
  const widthSum = sumColumnWidths(columns, computedColumns);
  const sumValid = isWidthSumValid(widthSum);
  const hasComputed = computedColumns.length > 0;

  return (
    <>
      <Form.Item label="Data key (campo del objeto con el array)">
        <Input
          value={value?.dataKey || ''}
          onChange={(e) => onChange({ ...value, dataKey: e.target.value })}
        />
      </Form.Item>
      <Divider>Columnas</Divider>
      <List
        dataSource={columns}
        renderItem={(col, idx) => (
          <List.Item>
            <Space style={{ width: '100%' }}>
              <Input
                placeholder="key"
                value={col.key}
                onChange={(e) => update(idx, { key: e.target.value })}
                style={{ width: 150 }}
              />
              <Input
                placeholder="título"
                value={col.title}
                onChange={(e) => update(idx, { title: e.target.value })}
                style={{ width: 200 }}
              />
              <Input
                placeholder="ancho"
                value={col.width}
                onChange={(e) => update(idx, { width: e.target.value })}
                style={{ width: 80 }}
              />
              <Select
                value={col.columnType || 'text'}
                onChange={(val) =>
                  update(idx, {
                    columnType: val,
                    dateFormat: val === 'date' ? col.dateFormat : undefined,
                  })
                }
                options={COLUMN_TYPE_OPTIONS}
                style={{ width: 110 }}
                title="Tipo de columna"
              />
              {col.columnType === 'date' ? (
                <Select
                  placeholder="Formato"
                  value={col.dateFormat || ''}
                  onChange={(val) => update(idx, { dateFormat: val || undefined })}
                  options={DATE_FORMAT_PRESETS}
                  style={{ width: 200 }}
                  title="Formato de fecha"
                />
              ) : null}
              <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(idx)} />
            </Space>
          </List.Item>
        )}
      />
      <Button icon={<PlusOutlined />} onClick={add} block>
        Agregar columna
      </Button>
      <div style={{ marginTop: 12 }}>
        <Alert
          type={sumValid ? 'success' : 'error'}
          showIcon
          message={
            <span>
              Suma de anchos: <strong>{widthSum.toFixed(1)}%</strong>
              {hasComputed ? ` (incluye ${computedColumns.length} columna(s) computada(s))` : ''}
            </span>
          }
          description={
            sumValid
              ? 'Las columnas cubren el ancho completo de la tabla.'
              : 'Los anchos deben sumar 100% antes de poder guardar la sección. Ajustá los valores para regularizar.'
          }
        />
      </div>
    </>
  );
}

function ImagesEditor({ value, onChange }) {
  return (
    <Form.Item label="Data key (array de imágenes en los datos)">
      <Input
        value={value?.dataKey || 'images'}
        onChange={(e) => onChange({ ...value, dataKey: e.target.value })}
      />
    </Form.Item>
  );
}

function SignaturesEditor({ value, onChange }) {
  return (
    <>
      <Form.Item label="Mostrar especialista">
        <Switch
          checked={value?.showSpecialist !== false}
          onChange={(checked) => onChange({ ...value, showSpecialist: checked })}
        />
      </Form.Item>
      <Form.Item label="Etiqueta especialista">
        <Input
          value={value?.specialistLabel || 'Especialista'}
          onChange={(e) => onChange({ ...value, specialistLabel: e.target.value })}
        />
      </Form.Item>
      <Divider />
      <Form.Item label="Mostrar recepción del cliente">
        <Switch
          checked={value?.showReceptionist !== false}
          onChange={(checked) => onChange({ ...value, showReceptionist: checked })}
        />
      </Form.Item>
      <Form.Item label="Etiqueta recepción">
        <Input
          value={value?.receptionistLabel || 'Recepción del Cliente'}
          onChange={(e) => onChange({ ...value, receptionistLabel: e.target.value })}
        />
      </Form.Item>
    </>
  );
}

// Portada editable: full-page con imagen de fondo opcional + overlay + logo +
// líneas de texto. Todos los strings soportan {var} contra data (incluye
// tenant_texts aplanados).
function PortraitEditor({ value, onChange, documentType, tenantId }) {
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <>
      <Form.Item label="Subtítulo (sobre el título, color primary)">
        <Input
          value={value?.subtitle || ''}
          placeholder="{portrait_tagline}"
          onChange={(e) => set({ subtitle: e.target.value })}
        />
      </Form.Item>
      <Form.Item label="Título principal">
        <Input
          value={value?.title || ''}
          placeholder="Necesidad de"
          onChange={(e) => set({ title: e.target.value })}
        />
      </Form.Item>
      <Form.Item label="Etiqueta de folio (opcional)">
        <Input
          value={value?.folioLabel || ''}
          placeholder="Reparación N°"
          onChange={(e) => set({ folioLabel: e.target.value })}
        />
      </Form.Item>
      <Form.Item label="Valor de folio">
        <Input
          value={value?.folioValue || ''}
          placeholder="{id}"
          onChange={(e) => set({ folioValue: e.target.value })}
        />
      </Form.Item>
      <Form.Item
        label="Texto libre bajo el folio (opcional)"
        tooltip="En el presupuesto se usa para el motivo del trabajo: {reason}. Si el documento no tiene valor, la línea no se imprime."
      >
        <Input
          value={value?.note || ''}
          placeholder="{reason}"
          onChange={(e) => set({ note: e.target.value })}
        />
      </Form.Item>
      <Form.Item label="Etiqueta del cliente (opcional)">
        <Input
          value={value?.clientLabel || ''}
          placeholder="Cliente:"
          onChange={(e) => set({ clientLabel: e.target.value })}
        />
      </Form.Item>
      <Form.Item label="Valor del cliente">
        <Input
          value={value?.clientValue || ''}
          placeholder="{client_name}"
          onChange={(e) => set({ clientValue: e.target.value })}
        />
      </Form.Item>
      <Divider />
      <Form.Item
        label="Imagen de fondo (URL absoluta o path relativo)"
        tooltip="Opcional. Se muestra full-page. Con un fondo decorativo activo, la imagen queda como textura por debajo del velo de color."
      >
        <Input
          value={value?.backgroundImage || ''}
          onChange={(e) => set({ backgroundImage: e.target.value })}
        />
      </Form.Item>
      <Form.Item
        label="Fondo decorativo"
        tooltip="Fondo dibujado con el color del tenant, sin necesidad de subir una imagen. 'Barras' es un fondo oscuro: el texto de la portada se invierte automáticamente a tonos claros."
      >
        <Select
          value={value?.backgroundPattern || 'none'}
          onChange={(v) => set({ backgroundPattern: v })}
          options={[
            { value: 'none', label: 'Sin fondo' },
            { value: 'bars', label: 'Barras escalonadas (fondo oscuro)' },
          ]}
        />
      </Form.Item>
      <Form.Item
        label="Color del overlay"
        tooltip="CSS color (rgba permitido). Ej: rgba(255,255,255,0.3)"
      >
        <Input
          value={value?.overlayColor || ''}
          placeholder="rgba(255,255,255,0.3)"
          onChange={(e) => set({ overlayColor: e.target.value })}
        />
      </Form.Item>
      <Form.Item
        label="Logo (URL absoluta o path relativo)"
        tooltip="Opcional. Si no se setea, cae a config.portraitLogo o config.logo del tenant."
      >
        <Input value={value?.logo || ''} onChange={(e) => set({ logo: e.target.value })} />
      </Form.Item>
      <VariablesHelper documentType={documentType} tenantId={tenantId} />
    </>
  );
}

// Header fijo por página: logo + línea izquierda + 3 líneas derecha.
function HeaderEditor({ value, onChange, documentType, tenantId }) {
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <>
      <Form.Item label="Logo (opcional, override)">
        <Input
          value={value?.logo || ''}
          placeholder="URL o path (fallback a config.logo)"
          onChange={(e) => set({ logo: e.target.value })}
        />
      </Form.Item>
      <Form.Item label="Línea izquierda (al lado del logo)">
        <Input
          value={value?.leftLine || ''}
          placeholder="{secondary_title}"
          onChange={(e) => set({ leftLine: e.target.value })}
        />
      </Form.Item>
      <Form.Item label="Línea derecha 1">
        <Input
          value={value?.rightLine1 || ''}
          placeholder="{right_header_1}"
          onChange={(e) => set({ rightLine1: e.target.value })}
        />
      </Form.Item>
      <Form.Item label="Línea derecha 2">
        <Input
          value={value?.rightLine2 || ''}
          placeholder="{right_header_2}"
          onChange={(e) => set({ rightLine2: e.target.value })}
        />
      </Form.Item>
      <Form.Item label="Línea derecha 3">
        <Input
          value={value?.rightLine3 || ''}
          placeholder="{right_header_3}"
          onChange={(e) => set({ rightLine3: e.target.value })}
        />
      </Form.Item>
      <Form.Item
        label="Color del borde inferior (hex)"
        tooltip="Fallback al primary color del tenant."
      >
        <Input
          value={value?.borderColor || ''}
          placeholder="#548235"
          onChange={(e) => set({ borderColor: e.target.value })}
        />
      </Form.Item>
      <VariablesHelper documentType={documentType} tenantId={tenantId} />
    </>
  );
}

// Footer fijo por página. `{pageNumber}` y `{totalPages}` son keys especiales
// que @react-pdf inyecta via render callback.
function FooterEditor({ value, onChange, documentType, tenantId }) {
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <>
      <Form.Item
        label="Línea izquierda"
        tooltip="Acepta {var} y las keys especiales {pageNumber} / {totalPages}."
      >
        <Input
          value={value?.leftLine || ''}
          placeholder="{footer}"
          onChange={(e) => set({ leftLine: e.target.value })}
        />
      </Form.Item>
      <Form.Item label="Línea izquierda 2 (opcional)">
        <Input
          value={value?.leftLine2 || ''}
          onChange={(e) => set({ leftLine2: e.target.value })}
        />
      </Form.Item>
      <Form.Item label="Línea central">
        <Input
          value={value?.centerLine || ''}
          placeholder="Página {pageNumber} de {totalPages}"
          onChange={(e) => set({ centerLine: e.target.value })}
        />
      </Form.Item>
      <Form.Item label="Línea derecha 1">
        <Input
          value={value?.rightLine1 || ''}
          placeholder="{footer_secondary}"
          onChange={(e) => set({ rightLine1: e.target.value })}
        />
      </Form.Item>
      <Form.Item label="Línea derecha 2 (opcional)">
        <Input
          value={value?.rightLine2 || ''}
          onChange={(e) => set({ rightLine2: e.target.value })}
        />
      </Form.Item>
      <Form.Item
        label="Color del borde superior (hex)"
        tooltip="Fallback al color border del tema."
      >
        <Input
          value={value?.borderColor || ''}
          placeholder="#e8e8e8"
          onChange={(e) => set({ borderColor: e.target.value })}
        />
      </Form.Item>
      <VariablesHelper documentType={documentType} tenantId={tenantId} />
    </>
  );
}

const CONFIG_EDITOR = {
  'data-grid': DataGridEditor,
  text: TextEditor,
  table: TableEditor,
  images: ImagesEditor,
  signatures: SignaturesEditor,
  portrait: PortraitEditor,
  header: HeaderEditor,
  footer: FooterEditor,
};

function SectionEditor({ section, documentType, tenantId, open, onClose, onSave }) {
  const [draft, setDraft] = useState(section ? cloneSection(section) : null);

  useEffect(() => {
    setDraft(section ? cloneSection(section) : null);
  }, [section]);

  if (!draft) return null;

  const ConfigEditor = CONFIG_EDITOR[draft.type];

  const handleConfigChange = (nextConfig) => setDraft({ ...draft, config: nextConfig });

  const handleSave = () => {
    onSave(draft);
    onClose();
  };

  // Bloquea "Aplicar" si la sección es una tabla cuyos anchos de columna no
  // suman 100% (ver utils/tableWidths.js).
  const canApply = isTableSectionWidthsValid(draft);

  return (
    <Drawer
      title={`Editar sección: ${draft.title}`}
      open={open}
      onClose={onClose}
      width={520}
      extra={
        <Space>
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            type="primary"
            onClick={handleSave}
            disabled={!canApply}
            title={canApply ? '' : 'Los anchos de las columnas deben sumar 100%'}
          >
            Aplicar
          </Button>
        </Space>
      }
    >
      <Form layout="vertical">
        <Form.Item label="Título">
          <Input
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          />
        </Form.Item>
        <Form.Item label="Visible">
          <Switch
            checked={draft.visible !== false}
            onChange={(checked) => setDraft({ ...draft, visible: checked })}
          />
        </Form.Item>
        <Divider>Configuración ({draft.type})</Divider>
        {ConfigEditor ? (
          <ConfigEditor
            value={draft.config}
            onChange={handleConfigChange}
            documentType={documentType}
            tenantId={tenantId}
          />
        ) : (
          <p>Tipo de sección no soportado: {draft.type}</p>
        )}
      </Form>
    </Drawer>
  );
}

export default SectionEditor;
